export type RealtimeVoiceQuestionnaireOption = {
  label: string;
  value: string;
  description?: string;
};

export type RealtimeVoiceQuestionnaireQuestion = {
  id: string;
  question: string;
  questionType:
    | 'single_choice'
    | 'multi_choice'
    | 'confirm'
    | 'number'
    | 'scale'
    | 'free_text'
    | 'file';
  required: boolean;
  options?: Array<RealtimeVoiceQuestionnaireOption>;
};

export type RealtimeVoiceQuestionnaireGroupAnswerValue =
  | { kind: 'choice'; value: string; label: string }
  | {
      kind: 'multi_choice';
      values: Array<{ value: string; label: string }>;
    }
  | { kind: 'confirm'; value: boolean }
  | { kind: 'free_text'; text: string };

export type RealtimeVoiceQuestionnaireAnswerValue =
  | RealtimeVoiceQuestionnaireGroupAnswerValue
  | { kind: 'number'; value: number }
  | { kind: 'scale'; value: number };

export type RealtimeVoiceQuestionnaireAnswerInput = {
  answerText?: string;
  toolCallId?: string;
  questionnaireNavigation?: 'previous';
  skipCurrentQuestion?: boolean;
  answerValue?: RealtimeVoiceQuestionnaireAnswerValue;
  groupAnswers?: Record<string, RealtimeVoiceQuestionnaireGroupAnswerValue>;
};

export type RealtimeVoiceQuestionnaireAnswerResult = {
  handled: boolean;
  message: string;
};

export type RealtimeVoiceQuestionnairePhase =
  | 'collecting'
  | 'confirming_next_batch'
  | 'confirming_submit';

export type ActiveRealtimeVoiceQuestionnaire = {
  orgThreadId: string;
  toolCallId: string;
  title?: string;
  description?: string;
  question: RealtimeVoiceQuestionnaireQuestion;
  questions?: Array<RealtimeVoiceQuestionnaireQuestion>;
  index: number;
  count: number;
  batchIndex?: number;
  batchCount?: number;
  isLastQuestion: boolean;
  phase?: RealtimeVoiceQuestionnairePhase;
  confirmationPrompt?: string;
  draftAnswerSummary?: string;
  draftAnswerSummaries?: Record<string, string>;
  promptKey: string;
  readAloudRequestKey?: string;
  answerCurrentStep: (
    input: RealtimeVoiceQuestionnaireAnswerInput
  ) => Promise<RealtimeVoiceQuestionnaireAnswerResult>;
};

let activeQuestionnaire: ActiveRealtimeVoiceQuestionnaire | null = null;
const listeners = new Set<() => void>();

function emitQuestionnaireChange() {
  for (const listener of listeners) {
    listener();
  }
}

export function registerActiveRealtimeVoiceQuestionnaire(
  questionnaire: ActiveRealtimeVoiceQuestionnaire
): () => void {
  activeQuestionnaire = questionnaire;
  emitQuestionnaireChange();
  return () => {
    if (activeQuestionnaire === questionnaire) {
      activeQuestionnaire = null;
      emitQuestionnaireChange();
    }
  };
}

export function subscribeActiveRealtimeVoiceQuestionnaire(
  listener: () => void
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getActiveRealtimeVoiceQuestionnaire(
  orgThreadId?: string | null
): ActiveRealtimeVoiceQuestionnaire | null {
  if (!activeQuestionnaire) return null;
  if (orgThreadId && activeQuestionnaire.orgThreadId !== orgThreadId) {
    return null;
  }
  return activeQuestionnaire;
}

function formatQuestionOptions(
  options: Array<RealtimeVoiceQuestionnaireOption> | undefined
): string {
  if (!options || options.length === 0) return 'none';
  return options
    .map(
      (option) =>
        `${option.label} [value=${option.value}]${
          option.description ? ` (${option.description})` : ''
        }`
    )
    .join('; ');
}

function normalizeQuestionnaireText(value: string | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function findQuestionOptionByText(
  question: RealtimeVoiceQuestionnaireQuestion,
  text: string
): RealtimeVoiceQuestionnaireOption | null {
  const normalizedText = normalizeQuestionnaireText(text);
  if (!normalizedText) return null;
  return (
    question.options?.find((option) =>
      [option.label, option.value].some(
        (candidate) => normalizeQuestionnaireText(candidate) === normalizedText
      )
    ) ?? null
  );
}

export function getRealtimeQuestionnaireConfirmation(
  text: string | undefined
): boolean | null {
  const normalizedText = normalizeQuestionnaireText(text);
  const firstWord = normalizedText.split(' ')[0]?.replace(/[^a-z']/g, '') ?? '';
  if (
    [
      'yes',
      'yeah',
      'yep',
      'yup',
      'sure',
      'ok',
      'okay',
      'confirm',
      'save',
      'submit',
      'continue',
      'proceed',
    ].includes(firstWord) ||
    normalizedText.startsWith('go ahead')
  ) {
    return true;
  }
  if (
    [
      'no',
      'nope',
      'nah',
      'cancel',
      'dont',
      "don't",
      'stay',
      'wait',
      'stop',
    ].includes(firstWord) ||
    normalizedText.startsWith('do not')
  ) {
    return false;
  }
  return null;
}

function coerceAnswerValueToGroupAnswer(
  value: RealtimeVoiceQuestionnaireAnswerValue | undefined
): RealtimeVoiceQuestionnaireGroupAnswerValue | undefined {
  if (!value) return undefined;
  if (
    value.kind === 'choice' ||
    value.kind === 'multi_choice' ||
    value.kind === 'confirm' ||
    value.kind === 'free_text'
  ) {
    return value;
  }
  return { kind: 'free_text', text: String(value.value) };
}

function coerceTextToGroupAnswer(
  question: RealtimeVoiceQuestionnaireQuestion,
  answerText: string | undefined
): RealtimeVoiceQuestionnaireGroupAnswerValue | undefined {
  const text = answerText?.trim();
  if (!text) return undefined;

  if (question.questionType === 'confirm') {
    const confirmation = getRealtimeQuestionnaireConfirmation(text);
    return confirmation == null
      ? undefined
      : { kind: 'confirm', value: confirmation };
  }

  if (question.questionType === 'single_choice') {
    const option = findQuestionOptionByText(question, text);
    return option
      ? { kind: 'choice', value: option.value, label: option.label }
      : { kind: 'free_text', text };
  }

  if (question.questionType === 'multi_choice') {
    const option = findQuestionOptionByText(question, text);
    return option
      ? {
          kind: 'multi_choice',
          values: [{ value: option.value, label: option.label }],
        }
      : { kind: 'free_text', text };
  }

  if (question.questionType === 'free_text') {
    return { kind: 'free_text', text };
  }

  return { kind: 'free_text', text };
}

export function formatActiveRealtimeVoiceQuestionnaireContext(
  questionnaire: ActiveRealtimeVoiceQuestionnaire | null
): string {
  if (!questionnaire) return '';
  const questions = questionnaire.questions;
  if (questions?.length) {
    const batchIndex = questionnaire.batchIndex ?? 0;
    const batchCount = questionnaire.batchCount ?? 1;
    const phase = questionnaire.phase ?? 'collecting';
    const questionLines = questions.flatMap((question, index) => [
      `questionId=${question.id} position=${questionnaire.index + index + 1}/${questionnaire.count} type=${question.questionType} required=${question.required}`,
      `Question: ${question.question}`,
      `Options: ${formatQuestionOptions(question.options)}`,
      questionnaire.draftAnswerSummaries?.[question.id]
        ? `Current draft answer: ${questionnaire.draftAnswerSummaries[question.id]}`
        : 'Current draft answer: none',
    ]);
    return [
      'Visible question state:',
      `The local visible question batch below is authoritative for voice answers. This is active batch ${batchIndex + 1}/${batchCount}.`,
      `toolCallId=${questionnaire.toolCallId} phase=${phase} finalBatch=${questionnaire.isLastQuestion}`,
      ...questionLines,
      'Use answer_current_question for the user answers. Include every answer understood in groupAnswers keyed by questionId. Do not call get_current_chat_context to answer this visible batch.',
      'If the user clearly pivots away, handle the new request instead. If unsure, ask whether to use it for the visible batch or start a new request.',
      'After answer_current_question succeeds, stay silent and wait for the next visible-batch prompt or user request.',
    ].join('\n');
  }
  const { question } = questionnaire;
  return [
    'Visible question state:',
    'The local visible question below is authoritative for voice answers. For stepwise flows, this is the local visible questionnaire step.',
    `toolCallId=${questionnaire.toolCallId} questionId=${question.id} step=${
      questionnaire.index + 1
    }/${questionnaire.count} final=${questionnaire.isLastQuestion}`,
    `type=${question.questionType} required=${question.required}`,
    `Question: ${question.question}`,
    `Options: ${formatQuestionOptions(question.options)}`,
    questionnaire.draftAnswerSummary
      ? `Current draft answer: ${questionnaire.draftAnswerSummary}`
      : 'Current draft answer: none',
    'Use answer_current_question for the user answer. Do not call get_current_chat_context to answer this visible question.',
    'If the user clearly pivots away, handle the new request instead. If unsure, ask: "Do you want me to use that as the answer to this question, or start a new request?"',
    'After answer_current_question succeeds, stay silent and wait for the next visible-question prompt or user request.',
  ].join('\n');
}

export function buildRealtimeQuestionnaireStepPrompt(
  questionnaire: ActiveRealtimeVoiceQuestionnaire
): string {
  const questions = questionnaire.questions;
  if (questions?.length) {
    const phase = questionnaire.phase ?? 'collecting';
    const batchIndex = questionnaire.batchIndex ?? 0;
    const batchCount = questionnaire.batchCount ?? 1;
    if (phase === 'confirming_next_batch') {
      return [
        '[SYSTEM VISIBLE QUESTION BATCH - not from user]',
        'The required answers in the current visible set are complete. Ask exactly one brief confirmation, then wait.',
        'Say naturally: "I have the answers for this set. Shall I continue to the next set?"',
        'When the user replies, call answer_current_question with their reply in answerText. Do not advance until they confirm.',
      ].join('\n');
    }
    if (phase === 'confirming_submit') {
      const confirmationPrompt =
        questionnaire.confirmationPrompt?.trim() ||
        'I have all the answers. Shall I submit them?';
      return [
        '[SYSTEM VISIBLE QUESTION BATCH - not from user]',
        'All required answers are complete. Ask exactly one brief submission confirmation, then wait.',
        `Say naturally: "${confirmationPrompt}"`,
        'When the user replies, call answer_current_question with their reply in answerText. Do not submit until they confirm.',
      ].join('\n');
    }

    const questionMetadata = questions
      .map(
        (question, index) =>
          `questionId=${question.id}; position=${questionnaire.index + index + 1}/${questionnaire.count}; questionType=${question.questionType}; required=${question.required}; question=${question.question}; options=${formatQuestionOptions(question.options)}`
      )
      .join('\n');
    return [
      '[SYSTEM VISIBLE QUESTION BATCH - not from user]',
      `A set of ${questions.length} questions is visible (set ${batchIndex + 1} of ${batchCount}). Do not call tools for this system message.`,
      `Internal state: toolCallId=${questionnaire.toolCallId}; phase=collecting; finalBatch=${questionnaire.isLastQuestion}.`,
      questionMetadata,
      'Give one short thematic summary of what this visible set covers, then wait. Do not enumerate or read every question or option aloud because the user can see them.',
      'When the user answers, call answer_current_question once. Put every answer you understood in groupAnswers keyed by questionId; partial answers are valid and must be retained.',
      'If required answers are still missing, ask only for those missing details. After answer_current_question succeeds, say nothing and wait for the next system prompt.',
    ].join('\n');
  }
  const { question } = questionnaire;
  const questionLine =
    questionnaire.count > 1
      ? `Question ${questionnaire.index + 1} of ${questionnaire.count}: ${
          question.question
        }`
      : `Question: ${question.question}`;
  return [
    '[SYSTEM VISIBLE QUESTION - not from user]',
    'A question widget is visible. Ask the visible decision out loud once, then wait for the user answer. Do not call tools for this system message.',
    `Internal state: toolCallId=${questionnaire.toolCallId}; questionId=${question.id}; questionType=${question.questionType}; required=${question.required}; step=${questionnaire.index + 1}/${questionnaire.count}; final=${questionnaire.isLastQuestion}; options=${formatQuestionOptions(question.options)}.`,
    questionLine,
    'Do not start with an acknowledgement. Rephrase the question naturally for the live context when it would sound better, especially for confirmation prompts, but preserve the required decision and options. Do not list options unless the question text itself asks you to. Do not narrate reasoning.',
    'When the user answers, call answer_current_question. Use answerValue or groupAnswers with the current questionId. If the answer matches an option by meaning, use that option value and label.',
    'If the user asks to skip an optional question, call answer_current_question with skipCurrentQuestion=true. If this required question is skipped, briefly say it needs an answer.',
    'After answer_current_question succeeds, say nothing.',
  ].join('\n');
}

export function getRealtimeVoiceAnswerValueText(
  value: RealtimeVoiceQuestionnaireAnswerValue | undefined
): string {
  if (!value) return '';
  if (value.kind === 'choice') return value.label.trim() || value.value.trim();
  if (value.kind === 'multi_choice') {
    return value.values
      .map((item) => item.label.trim() || item.value.trim())
      .filter(Boolean)
      .join(', ');
  }
  if (value.kind === 'confirm') return value.value ? 'Yes' : 'No';
  if (value.kind === 'number' || value.kind === 'scale') {
    return String(value.value);
  }
  return value.text.trim();
}

export function getRealtimeVoiceAnswerText(input: {
  answerText?: string;
  answerValue?: RealtimeVoiceQuestionnaireAnswerValue;
  groupAnswers?: Record<string, RealtimeVoiceQuestionnaireGroupAnswerValue>;
}): string {
  const groupAnswerText = Object.values(input.groupAnswers ?? {})
    .map(getRealtimeVoiceAnswerValueText)
    .filter(Boolean)
    .join(', ');
  if (groupAnswerText) return groupAnswerText;

  const answerValueText = getRealtimeVoiceAnswerValueText(input.answerValue);
  if (answerValueText) return answerValueText;

  return input.answerText?.trim() ?? '';
}

export function normalizeActiveRealtimeQuestionnaireAnswerInput(
  input: RealtimeVoiceQuestionnaireAnswerInput,
  currentQuestion: RealtimeVoiceQuestionnaireQuestion,
  activeQuestions: Array<RealtimeVoiceQuestionnaireQuestion> = [currentQuestion]
): RealtimeVoiceQuestionnaireAnswerInput {
  const currentStructuredAnswer = input.groupAnswers?.[currentQuestion.id];
  const answerValueGroupAnswer = coerceAnswerValueToGroupAnswer(
    input.answerValue
  );
  const shouldCoerceText =
    !input.questionnaireNavigation && !input.skipCurrentQuestion;
  const hasStructuredAnswer = Object.keys(input.groupAnswers ?? {}).length > 0;
  const textGroupAnswer =
    shouldCoerceText && !hasStructuredAnswer
      ? coerceTextToGroupAnswer(currentQuestion, input.answerText)
      : undefined;
  const currentGroupAnswer =
    currentStructuredAnswer ?? answerValueGroupAnswer ?? textGroupAnswer;
  const activeQuestionIds = new Set(
    activeQuestions.map((question) => question.id)
  );
  const activeGroupAnswers = Object.fromEntries(
    Object.entries(input.groupAnswers ?? {}).filter(([questionId]) =>
      activeQuestionIds.has(questionId)
    )
  );
  const groupAnswers = currentGroupAnswer
    ? { ...activeGroupAnswers, [currentQuestion.id]: currentGroupAnswer }
    : activeGroupAnswers;
  return {
    ...input,
    groupAnswers,
    answerText: getRealtimeVoiceAnswerText({
      ...input,
      groupAnswers,
    }),
    ...(currentGroupAnswer
      ? {
          questionnaireNavigation: undefined,
          skipCurrentQuestion: undefined,
        }
      : {}),
  };
}

export function getRealtimeQuestionnaireToolResponseText(message: string) {
  return message.startsWith('Internal success:') ? '' : message;
}
